import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getDatabase, ref, query, limitToLast, onChildAdded, get } from 'firebase/database';
import { getRealtimeToken } from 'shared/common.api';
import { snackbarService } from 'components/mui/snackbar/snackbar-service';
import { UserStateContext } from './user-state-context';
import { NotificationsContext } from './notifications-context';
import { CustomerDrawerContext } from './customer-drawer-context';
import { SoftphoneContext } from './softphone-context';
export const RealtimeContext = createContext();

// initializeApp throws if called twice and browser-notifications.js also calls it.
const firebaseApp = () => getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);

/**
 * Every button a backend notification is allowed to put on a toast.
 *
 * This is the contract the backend writes against - it names a `kind` here and we run the
 * matching function. Anything not in this map is skipped, so the backend can only pick from
 * behavior we already have, never describe new behavior. Adding a kind here is what makes it
 * available to every notification type at once, with no further frontend work.
 *
 * Keep the docs on send_notification's `actions` argument (backend, app/notification/tasks.py)
 * in step with this map.
 */
const ACTIONS = {
  customer_drawer: (action, ctx) => ctx.openCustomerDrawer(action.customer_id, action.tab || 'summary'),
  navigate: (action, ctx) => ctx.history.push(action.path)
};

/**
 * Subscribes to this user's Firebase Realtime Database channel so the backend can
 * nudge open tabs in real time. This is a data subscription, not a push notification,
 * so it needs no notification permission - only that the app is open.
 *
 * Events are nudges: they carry just enough to show a toast, and anything that needs
 * to be authoritative is refetched through the normal API.
 */
export const RealtimeContextProvider = ({
  children
}) => {
  const {
    user,
    asCompany
  } = useContext(UserStateContext);
  const {
    refreshNewCount
  } = useContext(NotificationsContext);
  const {
    openCustomerDrawer
  } = useContext(CustomerDrawerContext);
  const {
    connected: softphoneConnected
  } = useContext(SoftphoneContext) || {};
  // useHistory rather than the `history` singleton from root.component: root.component
  // renders these providers, so importing it here would be a cycle.
  const history = useHistory();
  const handlersRef = useRef({});

  // Everything the listener reads goes through this ref. openCustomerDrawer is a plain
  // function rebuilt on every render of its provider, so depending on it directly would
  // tear down and re-establish the subscription constantly.
  const latest = useRef({});
  latest.current = {
    asCompany,
    refreshNewCount,
    openCustomerDrawer,
    history,
    softphoneConnected
  };

  // Let any component register for an event type without re-subscribing the socket.
  const subscribe = useCallback((eventType, handler) => {
    if (!handlersRef.current[eventType]) {
      handlersRef.current[eventType] = [];
    }
    handlersRef.current[eventType].push(handler);
    return () => {
      handlersRef.current[eventType] = (handlersRef.current[eventType] || []).filter(h => h !== handler);
    };
  }, []);

  // The toast is built entirely from what the backend sent - the text it wrote and the
  // buttons it asked for - so a new kind of notification needs no code here.
  const showNotification = useCallback((eventType, payload) => {
    const {
      asCompany: company,
      refreshNewCount: refresh,
      softphoneConnected
    } = latest.current;

    // The channel is per-user and a user can span companies, so drop events for a
    // company they aren't currently looking at.
    if (payload.company_id && payload.company_id !== company?.id) {
      return;
    }

    // Calls get no toast of their own: an incoming one is the call pop's (CallPopHost), and
    // an outgoing one is already on screen in the softphone when that is how it was placed.
    // An event without a direction predates the backend sending one, so it counts as
    // incoming rather than risking a toast and a card for the same call.
    if (eventType === 'voice_call.incoming' && (payload.direction !== 'outgoing' || softphoneConnected)) {
      refresh();
      return;
    }
    const actions = (payload.actions || []).filter(action => ACTIONS[action.kind]);

    // No `type` here on purpose: the snackbar only renders an Alert (which swallows
    // `action`) when one is set, and we need the action buttons.
    snackbarService.popup({
      duration: 10000,
      message: [payload.title, payload.body].filter(Boolean).join(' - '),
      action: <React.Fragment>
          {actions.map((action, i) => <Button key={i} size="small" onClick={() => {
          snackbarService.close();
          ACTIONS[action.kind](action, latest.current);
        }}>
              <Typography sx={{
            textTransform: 'none',
            color: '#53A6D6'
          }}>{action.label}</Typography>
            </Button>)}
          <IconButton size="small" color="white" onClick={() => snackbarService.close()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </React.Fragment>
    });
    refresh();
  }, []);
  useEffect(() => {
    if (!user?.id) {
      return;
    }
    let cancelled = false;
    let unsubscribe;
    const connect = async () => {
      const {
        data
      } = await getRealtimeToken();
      if (cancelled || !data.enabled) {
        return;
      }
      const app = firebaseApp();
      await signInWithCustomToken(getAuth(app), data.token);
      if (cancelled) {
        return;
      }
      const channel = ref(getDatabase(app, data.database_url), data.path);

      // Whatever is already in the channel is backlog, not news. Push keys sort
      // chronologically, so the newest existing key is all we need to tell old from
      // new - and unlike comparing timestamps, it doesn't depend on the browser clock
      // agreeing with Firebase's.
      let lastSeenKey = null;
      (await get(query(channel, limitToLast(1)))).forEach(child => {
        lastSeenKey = child.key;
      });
      if (cancelled) {
        return;
      }
      unsubscribe = onChildAdded(query(channel, limitToLast(10)), snapshot => {
        if (lastSeenKey && snapshot.key <= lastSeenKey) {
          return;
        }
        lastSeenKey = snapshot.key;
        const event = snapshot.val() || {};
        const payload = event.data || {};

        // A title is what makes an event something to show. Events without one are data
        // for whatever registered a handler below, and render nothing on their own.
        if (payload.title) {
          showNotification(event.type, payload);
        }
        (handlersRef.current[event.type] || []).forEach(handler => {
          try {
            handler(payload, event);
          } catch (e) {
            console.error('Realtime handler failed for', event.type, e);
          }
        });
      },
      // Fires when the security rules reject the read, which is otherwise silent.
      error => console.error(`Realtime channel ${data.path} rejected:`, error));
    };
    connect().catch(e => {
      if (e?.code === 'auth/configuration-not-found') {
        console.error('Realtime: Firebase Authentication is not enabled on this project.', e);
      } else {
        console.error('Realtime subscribe failed', e);
      }
    });
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [user?.id, showNotification]);
  return <RealtimeContext.Provider value={{
    subscribe
  }}>{children}</RealtimeContext.Provider>;
};